import "@andypf/json-viewer";

import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnDestroy,
  ViewEncapsulation,
  type WritableSignal,
  computed,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { DatePipe } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogState,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatTabGroup, MatTab } from "@angular/material/tabs";
import { MatSelect, MatOption, MatSelectChange } from "@angular/material/select";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from "@angular/material/expansion";
import { MatTooltip } from "@angular/material/tooltip";
import { MatChip } from "@angular/material/chips";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport, CdkVirtualForOf, CdkFixedSizeVirtualScroll } from "@angular/cdk/scrolling";
import { FormsModule } from "@angular/forms";

import { DebugStreamService, type DebugEvent, type PipelineDebugEvent } from "../../../services/debug-stream.service";
import { AgentDebugEventDetailComponent } from "../agent-debug-event-detail/agent-debug-event-detail.component";
import { AgentKitService } from "../../../../../agent-ui";
import { WorkspaceIndexCardComponent } from "../workspace-index-card/workspace-index-card.component";
import { ContentBlockParserService } from "../../../services/content-block-parser.service";
import { DebugModeService } from "../../../services/debug-mode.service";

export interface AgentDebugDialogData {
  agentKit: AgentKitService;
  agentId: string;
}

@Component({
  selector: "lib-ai-agent-debug-dialog",
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [DebugModeService],
  imports: [
    DatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogClose,
    MatTabGroup,
    MatTab,
    MatSelect,
    MatOption,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatTooltip,
    MatChip,
    CdkDrag,
    CdkDragHandle,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
    CdkFixedSizeVirtualScroll,
    FormsModule,
    WorkspaceIndexCardComponent,
  ],
  templateUrl: "./agent-debug-dialog.component.html",
  styleUrls: ["./agent-debug-dialog.component.scss"],
})
export class AgentDebugDialogComponent implements OnDestroy {
  protected readonly debugService = inject(DebugStreamService);

  private readonly parser = inject(ContentBlockParserService);

  private readonly dialog = inject(MatDialog);

  private readonly streamViewport = viewChild<CdkVirtualScrollViewport>("streamViewport");

  private readonly pipelineViewport = viewChild<CdkVirtualScrollViewport>("pipelineViewport");

  protected readonly selectedTypes = signal<string[]>([]);

  protected selectedTypesModel: string[] = [];

  protected readonly searchQuery = signal("");

  protected readonly searchMatchIndex = signal(-1);

  protected readonly pipelineSearchQuery = signal("");

  protected readonly pipelineSearchMatchIndex = signal(-1);

  protected readonly availableTypes = computed(() =>
    Array.from(new Set(this.debugService.events().map((e) => e.type))).sort(),
  );

  protected readonly filteredEvents = computed(() => {
    const sel = this.selectedTypes();
    const all = this.debugService.events();
    return sel.length === 0 ? all : all.filter((e) => sel.includes(e.type));
  });

  protected readonly searchMatches = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return [] as number[];
    const events = this.filteredEvents();
    const matches: number[] = [];
    for (let i = 0; i < events.length; i++) {
      if (events[i].searchText.includes(q)) matches.push(i);
    }
    return matches;
  });

  protected readonly pipelineEvents = computed(() => this.debugService.pipelineEvents());

  protected readonly pipelineSearchMatches = computed(() => {
    const q = this.pipelineSearchQuery().toLowerCase().trim();
    if (!q) return [] as number[];
    const events = this.pipelineEvents();
    const matches: number[] = [];
    for (let i = 0; i < events.length; i++) {
      if (events[i].searchText.includes(q)) matches.push(i);
    }
    return matches;
  });

  protected readonly latestState = computed(() => this.debugService.latestState());

  protected readonly workspaceIndexSnapshot = computed(() =>
    this.parser.parseWorkspaceIndexSnapshot(this.debugService.latestState()),
  );

  protected readonly latestContext = computed(() => this.debugService.latestContext());

  protected readonly runtimeInfo = computed(() => ({
    runtimeUrl: this.data.agentKit.runtimeUrl(),
    headers: this.data.agentKit.headers(),
  }));

  private detailRef: MatDialogRef<AgentDebugEventDetailComponent> | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) readonly data: AgentDebugDialogData) {}

  ngOnDestroy(): void {
    this.detailRef?.close();
  }

  protected onTypesChange(event: MatSelectChange): void {
    const value: string[] = event.value;
    if (value.includes("__ALL__")) {
      const allTypes = this.availableTypes();
      const wasAll = this.selectedTypesModel.length === allTypes.length;
      this.selectedTypesModel = wasAll ? [] : [...allTypes];
      this.selectedTypes.set(this.selectedTypesModel);
    } else {
      this.selectedTypesModel = value;
      this.selectedTypes.set(value);
    }
  }

  protected isAllSelected(): boolean {
    const all = this.availableTypes();
    return all.length > 0 && this.selectedTypesModel.length === all.length;
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchMatchIndex.set(-1);
    if (value.trim()) this.goToNextMatch();
  }

  protected goToNextMatch(): void {
    this.navigateMatches(this.searchMatches(), this.searchMatchIndex, 1, (i) =>
      this.streamViewport()?.scrollToIndex(i, "smooth"),
    );
  }

  protected goToPrevMatch(): void {
    this.navigateMatches(this.searchMatches(), this.searchMatchIndex, -1, (i) =>
      this.streamViewport()?.scrollToIndex(i, "smooth"),
    );
  }

  protected onPipelineSearchChange(value: string): void {
    this.pipelineSearchQuery.set(value);
    this.pipelineSearchMatchIndex.set(-1);
    if (value.trim()) this.goToNextPipelineMatch();
  }

  protected goToNextPipelineMatch(): void {
    this.navigateMatches(this.pipelineSearchMatches(), this.pipelineSearchMatchIndex, 1, (i) =>
      this.pipelineViewport()?.scrollToIndex(i, "smooth"),
    );
  }

  protected goToPrevPipelineMatch(): void {
    this.navigateMatches(this.pipelineSearchMatches(), this.pipelineSearchMatchIndex, -1, (i) =>
      this.pipelineViewport()?.scrollToIndex(i, "smooth"),
    );
  }

  private navigateMatches(
    matches: number[],
    indexSignal: WritableSignal<number>,
    direction: 1 | -1,
    scrollFn: (index: number) => void,
  ): void {
    if (!matches.length) return;
    const current = indexSignal();
    const next =
      direction === 1
        ? current < matches.length - 1
          ? current + 1
          : 0
        : current > 0
        ? current - 1
        : matches.length - 1;
    indexSignal.set(next);
    scrollFn(matches[next]);
  }

  protected trackById(_: number, e: DebugEvent): number {
    return e.id;
  }

  protected trackPipelineById(_: number, e: PipelineDebugEvent): number {
    return e.id;
  }

  // ── M3 chip colors for event types (stream + merged lifecycle rows) ────────

  protected chipBg(type: string): string {
    if (type.includes("ERROR") || type.includes("FAIL")) return "var(--mat-sys-error-container)";
    if (type.startsWith("RUN_")) return "var(--mat-sys-primary-container)";
    if (type.startsWith("TEXT_MESSAGE_") || type.startsWith("MESSAGES_")) return "var(--mat-sys-secondary-container)";
    if (type.startsWith("TOOL_CALL_")) return "var(--mat-sys-tertiary-container)";
    if (type === "SNAPSHOT") return "var(--mat-sys-tertiary-container)";
    return "var(--mat-sys-surface-variant)";
  }

  protected chipFg(type: string): string {
    if (type.includes("ERROR") || type.includes("FAIL")) return "var(--mat-sys-on-error-container)";
    if (type.startsWith("RUN_")) return "var(--mat-sys-on-primary-container)";
    if (type.startsWith("TEXT_MESSAGE_") || type.startsWith("MESSAGES_")) return "var(--mat-sys-on-secondary-container)";
    if (type.startsWith("TOOL_CALL_")) return "var(--mat-sys-on-tertiary-container)";
    if (type === "SNAPSHOT") return "var(--mat-sys-on-tertiary-container)";
    return "var(--mat-sys-on-surface-variant)";
  }

  // ── Content summary ────────────────────────────────────────────────────────
  // Lifecycle rows carry a pre-formatted summary; stream rows fall back to generic key-based summarization.

  protected summarize(e: DebugEvent): string {
    return e.summary !== undefined ? e.summary : this.summarizeContent(e.content);
  }

  protected summarizePipeline(e: PipelineDebugEvent): string {
    return this.summarizeContent(e.content);
  }

  private summarizeContent(content: unknown): string {
    if (content === null) return "";
    const c = content as any;
    if (typeof c !== "object" || Array.isArray(c)) return String(c).slice(0, 100);

    const entries = Object.entries(c).filter(([k]) => k !== "type");
    if (!entries.length) return "";

    return entries
      .slice(0, 4)
      .map(([k, v]) => {
        if (v === null || v === undefined) return `${k}: —`;
        if (Array.isArray(v)) return `${k}[${v.length}]`;
        if (typeof v === "object") {
          const childKeys = Object.keys(v as object).filter((ck) => ck !== "type");
          return childKeys.length ? `${k} {${childKeys.slice(0, 4).join(", ")}}` : `${k} {}`;
        }
        const s = String(v);
        return `${k}: ${s.length > 40 ? s.slice(0, 40) + "…" : s}`;
      })
      .join("  ·  ");
  }

  // ── Detail dialog (single shared instance) ────────────────────────────────

  private openDetailDialog(data: unknown): void {
    if (this.detailRef && this.detailRef.getState() !== MatDialogState.CLOSED) {
      this.detailRef.componentInstance?.setData(data);
      return;
    }
    this.detailRef = this.dialog.open(AgentDebugEventDetailComponent, {
      data,
      panelClass: "event-detail-dialog",
      hasBackdrop: false,
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
      width: "700px",
      maxWidth: "none",
      position: { top: "120px", right: "20px" },
    });
    this.detailRef.afterClosed().subscribe(() => {
      this.detailRef = null;
    });
  }

  protected openDetail(e: DebugEvent): void {
    if (e.content !== null) this.openDetailDialog(e.content);
  }

  protected openPipelineDetail(e: PipelineDebugEvent): void {
    if (e.content !== null) this.openDetailDialog(e.content);
  }

  protected async copyContent(event: DebugEvent): Promise<void> {
    try {
      await navigator.clipboard.writeText(event.content !== null ? JSON.stringify(event.content, null, 2) : "");
    } catch {
      /* clipboard not available */
    }
  }

  protected async copyPipelineContent(event: PipelineDebugEvent): Promise<void> {
    try {
      await navigator.clipboard.writeText(event.content !== null ? JSON.stringify(event.content, null, 2) : "");
    } catch {
      /* clipboard not available */
    }
  }
}
